import React from 'react'
import PropTypes from 'prop-types'
import OpponentAvailabilityListView from '../../../scheduling/OpponentAvailabilityListView'
import featureFlags from '../../../../config/featureFlags'

export default function PlayerAvailabilitySection({
  fromLeague,
  leagueId,
  leagueName,
  divisionName,
  availabilityLoading,
  availabilityError,
  processedOpponents,
  handleProposeMatch,
  handleDirectProposeMatch,
  hasUserOverlap,
  getMatchProposalForOpponent,
  getLatestProposalForOpponent,
  getScheduledMatchForOpponent,
  getExistingMatchWithOpponent,
  currentUserPlayerId,
  allMatches,
}) {
  if (!fromLeague || !leagueId) {
    return null
  }

  return (
    <div className="row mb-4">
      <div className="col-12">
        <div
          className="card border-0 shadow-sm"
          role="region"
          aria-label="Opponent availability for scheduling"
        >
          <div className="card-header bg-light border-0">
            <div className="d-flex align-items-center">
              <span className="me-2" style={{ fontSize: '1.5rem' }}>
                📅
              </span>
              <h4 className="mb-0">
                Availability in {leagueName || 'League'}
                {divisionName && ` - ${divisionName}`}
              </h4>
            </div>
            <p className="text-muted mb-0 mt-1 small">
              Schedule a match by proposing a time from their availability
            </p>
          </div>
          <div className="card-body">
            {availabilityLoading ? (
              <div className="text-center py-3">
                <div
                  className="spinner-border spinner-border-sm mb-2"
                  role="status"
                >
                  <span className="visually-hidden">
                    Loading availability...
                  </span>
                </div>
                <p className="text-muted mb-0">Loading availability...</p>
              </div>
            ) : availabilityError ? (
              <div className="alert alert-warning mb-0">
                <p className="mb-0">{availabilityError}</p>
              </div>
            ) : (
              <OpponentAvailabilityListView
                opponents={processedOpponents}
                onProposeMatch={handleProposeMatch}
                onDirectProposeMatch={handleDirectProposeMatch}
                hasUserOverlap={hasUserOverlap}
                getMatchProposalForOpponent={getMatchProposalForOpponent}
                getLatestProposalForOpponent={getLatestProposalForOpponent}
                getScheduledMatchForOpponent={getScheduledMatchForOpponent}
                getExistingMatchWithOpponent={getExistingMatchWithOpponent}
                showEmptyState={true}
                emptyMessage="This player has no upcoming availability in this league."
                currentPlayerId={currentUserPlayerId}
                allMatches={allMatches}
                directProposalEnabled={featureFlags.DIRECT_MATCH_PROPOSALS}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

PlayerAvailabilitySection.propTypes = {
  fromLeague: PropTypes.bool,
  leagueId: PropTypes.number,
  leagueName: PropTypes.string,
  divisionName: PropTypes.string,
  availabilityLoading: PropTypes.bool.isRequired,
  availabilityError: PropTypes.string,
  processedOpponents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      user_id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      // Opposing-team identity for doubles; null/undefined for singles.
      // Mirrors the shape consumed by OpponentAvailabilityListView.
      team: PropTypes.shape({
        team_id: PropTypes.number,
        display_name: PropTypes.string,
        players: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number.isRequired,
            user_id: PropTypes.number.isRequired,
            first_name: PropTypes.string.isRequired,
            last_name: PropTypes.string.isRequired,
          }),
        ),
      }),
      slots: PropTypes.array,
    }),
  ).isRequired,
  handleProposeMatch: PropTypes.func.isRequired,
  handleDirectProposeMatch: PropTypes.func.isRequired,
  hasUserOverlap: PropTypes.func.isRequired,
  getMatchProposalForOpponent: PropTypes.func.isRequired,
  getLatestProposalForOpponent: PropTypes.func.isRequired,
  getScheduledMatchForOpponent: PropTypes.func.isRequired,
  getExistingMatchWithOpponent: PropTypes.func.isRequired,
  currentUserPlayerId: PropTypes.number,
  allMatches: PropTypes.array.isRequired,
}
